import { Component, OnDestroy, OnInit } from '@angular/core';
import { DatePipe, DecimalPipe, NgTemplateOutlet } from '@angular/common';
import { Subscription, timer } from 'rxjs';
import { FeatureSwitchService } from '../../_services/feature-switch/feature-switch.service';
import { LiveActivityService } from '../../_services/live-activity/live-activity.service';
import { FeatureSwitchKeys } from '../../_constants/feature-switch-keys';
import { roleLabel } from '../../_constants/role-labels';
import {
  formatDuration, jobDescription, jobLabel, operationLabel, processLabel, requestLabel, statusLabel
} from '../../_constants/live-activity-labels';
import { AdminEmailComponent } from '../../shared/admin-ux/admin-email.component';
import {
  LiveActivityAction, LiveActivityAuthRejection, LiveActivityOperation, LiveActivitySummary, LiveActivityUser
} from '../../_models/admin/live-activity.models';

/** Reads an API timestamp as UTC. The API sends DateTime values from SQL without a zone designator. */
export function parseUtc(value: string | null | undefined): Date | null {
  if (!value) {
    return null;
  }
  const hasZone = /([zZ]|[+-]\d{2}:?\d{2})$/.test(value);
  const date = new Date(hasZone ? value : value + 'Z');
  return isNaN(date.getTime()) ? null : date;
}

/** "just now", "2 min ago", "3 h ago", "2 d ago". */
export function timeAgo(value: string | null | undefined, now: number): string {
  const date = parseUtc(value);
  if (!date) {
    return '';
  }
  const seconds = Math.max(0, Math.round((now - date.getTime()) / 1000));
  if (seconds < 60) {
    return 'just now';
  }
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    return `${minutes} min ago`;
  }
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return `${hours} h ago`;
  }
  return `${Math.floor(hours / 24)} d ago`;
}

/**
 * Live activity — who is using the system now, what they are doing, and which operations are slow or
 * failing, read from the API's action log (dbo.ActionLogs).
 *
 * Gated on the LiveActivityView feature and the admin.live-activity switch. Read-only: nothing here changes
 * anything. The summary refreshes every 30 seconds until paused; the timer stops when the page is left.
 * A user's recent actions come without request bodies, and with the IP address masked by the API.
 */
@Component({
  selector: 'live-activity',
  standalone: true,
  imports: [DatePipe, DecimalPipe, NgTemplateOutlet, AdminEmailComponent],
  templateUrl: 'live-activity.page.html'
})
export class LiveActivityPage implements OnInit, OnDestroy {

  public static readonly refreshIntervalMs = 30000;
  /** A p95 above this is shown in red. */
  public static readonly slowP95Ms = 2000;
  /** A failure rate above this percentage is shown in red. */
  public static readonly highFailurePercent = 5;

  public readonly windows = [
    { minutes: 15, label: '15 min' },
    { minutes: 60, label: '1 h' },
    { minutes: 240, label: '4 h' }
  ];

  public available: boolean | null = null;
  public minutes: number = 15;
  public paused: boolean = false;
  public loading: boolean = false;
  public error: string | null = null;
  public summary: LiveActivitySummary | null = null;
  public refreshedAt: Date | null = null;
  /** The clock "2 min ago" is measured against: the time of the last refresh. */
  public now: number = Date.now();

  public selectedUser: LiveActivityUser | null = null;
  public recent: LiveActivityAction[] = [];
  public recentLoading: boolean = false;
  public recentError: string | null = null;

  private timerSubscription: Subscription | null = null;
  private summaryRequest: Subscription | null = null;
  private recentRequest: Subscription | null = null;

  constructor(
    private featureSwitches: FeatureSwitchService,
    private service: LiveActivityService
  ) { }

  ngOnInit(): void {
    // canUseGuard has already required LiveActivityView on the route.
    this.featureSwitches.isEnabled(FeatureSwitchKeys.liveActivity).subscribe(on => {
      this.available = on;
      if (on) {
        this.startTimer();
      }
    });
  }

  ngOnDestroy(): void {
    this.stopTimer();
    this.summaryRequest?.unsubscribe();
    this.recentRequest?.unsubscribe();
  }

  public get timerRunning(): boolean {
    return this.timerSubscription !== null;
  }

  public setWindow(minutes: number): void {
    if (minutes === this.minutes) {
      return;
    }
    this.minutes = minutes;
    this.restartTimer();
  }

  public togglePause(): void {
    this.paused = !this.paused;
    if (this.paused) {
      this.stopTimer();
    } else {
      this.startTimer();
    }
  }

  /** Refresh now; the automatic refresh, when running, restarts its 30 seconds from here. */
  public refreshNow(): void {
    this.restartTimer();
  }

  public selectUser(user: LiveActivityUser): void {
    if (this.selectedUser?.userId === user.userId) {
      this.selectedUser = null;
      this.recent = [];
      this.recentRequest?.unsubscribe();
      return;
    }
    this.selectedUser = user;
    this.recent = [];
    this.loadRecent();
  }

  public userName(user: LiveActivityUser): string {
    const name = [user.firstName, user.lastName].filter(n => !!n).join(' ');
    return name || user.email || `User ${user.userId}`;
  }

  /** "IT admin, Tuition consultant" from the role codes. */
  public roleNames(user: LiveActivityUser): string {
    return (user.roles ?? []).map(roleLabel).join(', ');
  }

  /** Internal or External for a signed-in user; "—" when the API could not say. */
  public userTypeLabel(user: LiveActivityUser): string {
    return user.userType || '—';
  }

  /** Internal or External for a rejected request; "Not signed in" when the token named no one. */
  public rejectedType(r: LiveActivityAuthRejection): string {
    return r.userId == null ? 'Not signed in' : (r.userType || '—');
  }

  /** Who a rejected request was: a name only when the API proved the token was ours, otherwise "Not known". */
  public rejectedBy(r: LiveActivityAuthRejection): string {
    if (r.userId == null) {
      return 'Not known';
    }
    if (r.userId === 0) {
      return 'A learner';
    }
    const name = [r.firstName, r.lastName].filter(n => !!n).join(' ');
    return name || `User ${r.userId}`;
  }

  /**
   * Why a request was refused, in words (RejectedTokenInspector's reasons). 'renewed' is an expired sign-in the
   * app renewed straight away (the API sees a successful call from the same person within 5 minutes), which is
   * routine. 'expired' with no renewal is someone who had to sign in again, or had left. An unknown reason is
   * shown as it is.
   */
  public reasonLabel(reason: string | null | undefined): string {
    switch (reason) {
      case 'renewed': return 'Expired, renewed automatically';
      case 'expired': return 'Expired, not renewed';
      case 'no token': return 'Not signed in';
      case 'malformed': return 'Sign-in unreadable';
      case 'not yet valid': return 'Sign-in not valid yet';
      case 'bad signature': return 'Sign-in not issued by us';
      case 'wrong issuer or audience': return 'Sign-in meant for another system';
      case 'invalid': return 'Sign-in not valid';
      case 'rejected after sign-in': return 'Refused after sign-in';
      default: return reason || '-';
    }
  }

  public ago(value: string): string {
    return timeAgo(value, this.now);
  }

  public utc(value: string): Date | null {
    return parseUtc(value);
  }

  /** "tutoraccounts.ensure, email.outbound" from the comma-separated switch list the API sends. */
  public switches(keys: string | null | undefined): string {
    return (keys ?? '').split(',').map(k => k.trim()).filter(k => k).join(', ');
  }

  /** "Create missing tutor accounts, All email": the same list by the names System Controls uses. */
  public switchNames(keys: string | null | undefined): string {
    return (keys ?? '').split(',').map(k => k.trim()).filter(k => k).map(processLabel).join(', ');
  }

  /** The friendly name of an operation (an action log type). */
  public op(actionType: string | null | undefined): string {
    return operationLabel(actionType);
  }

  /** The friendly name of a request known only by method and path. */
  public requestName(method: string | null | undefined, path: string | null | undefined): string {
    return requestLabel(method, path);
  }

  /** "GET /api/Placement/5", for a tooltip. */
  public request(method: string | null | undefined, path: string | null | undefined): string {
    return [method, path].filter(p => !!p).join(' ');
  }

  public job(name: string): string {
    return jobLabel(name);
  }

  public jobHelp(name: string): string {
    return jobDescription(name);
  }

  public status(statusCode: number | null | undefined, success?: boolean | null): string {
    return statusLabel(statusCode, success);
  }

  /** A status worth flagging on a user's last action: a refusal or a server error. */
  public isFailedStatus(statusCode: number | null | undefined): boolean {
    return statusCode != null && statusCode >= 400;
  }

  public duration(ms: number | null | undefined): string {
    return formatDuration(ms);
  }

  /** "15 min", "1 h" or "4 h": the window in use. */
  public get windowLabel(): string {
    return this.windows.find(w => w.minutes === this.minutes)?.label ?? `${this.minutes} min`;
  }

  public isSlow(ms: number | null | undefined): boolean {
    return ms != null && ms > LiveActivityPage.slowP95Ms;
  }

  public isHighFailure(percent: number | null | undefined): boolean {
    return percent != null && percent > LiveActivityPage.highFailurePercent;
  }

  public operationFailurePercent(op: LiveActivityOperation): number {
    return op.requestCount > 0 ? (100 * op.failureCount) / op.requestCount : 0;
  }

  private restartTimer(): void {
    if (this.paused) {
      this.refresh();
      return;
    }
    this.stopTimer();
    this.startTimer();
  }

  private startTimer(): void {
    this.stopTimer();
    this.refresh();
    const every = LiveActivityPage.refreshIntervalMs;
    this.timerSubscription = timer(every, every).subscribe(() => this.refresh());
  }

  private stopTimer(): void {
    this.timerSubscription?.unsubscribe();
    this.timerSubscription = null;
  }

  private refresh(): void {
    this.summaryRequest?.unsubscribe();
    this.loading = true;
    this.summaryRequest = this.service.getSummary(this.minutes).subscribe({
      next: summary => {
        this.summary = summary;
        this.now = Date.now();
        this.refreshedAt = new Date(this.now);
        this.error = null;
        this.loading = false;
        if (this.selectedUser) {
          this.selectedUser = summary.activeUsers.find(u => u.userId === this.selectedUser!.userId) ?? this.selectedUser;
          this.loadRecent();
        }
      },
      error: err => {
        this.error = err?.message || 'The activity summary could not be loaded.';
        this.loading = false;
      }
    });
  }

  private loadRecent(): void {
    const user = this.selectedUser;
    if (!user) {
      return;
    }
    this.recentRequest?.unsubscribe();
    this.recentLoading = true;
    this.recentError = null;
    this.recentRequest = this.service.getUserRecent(user.userId, 50).subscribe({
      next: actions => {
        this.recent = actions;
        this.recentLoading = false;
      },
      error: err => {
        this.recentError = err?.message || 'This user\'s recent actions could not be loaded.';
        this.recentLoading = false;
      }
    });
  }
}
